import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <section className="relative py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-lg text-gray-300">
              How MGL Experiences Ltd collects, uses, and protects your information in accordance with UK data protection laws.
            </p>
          </div>
        </div>
      </section>

      <div className="section-container">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">On This Page</h3>
                <nav className="space-y-2">
                  <a href="#overview" className="block text-gray-600 hover:text-primary">Overview</a>
                  <a href="#information" className="block text-gray-600 hover:text-primary">Information We Collect</a>
                  <a href="#usage" className="block text-gray-600 hover:text-primary">How We Use Information</a>
                  <a href="#sharing" className="block text-gray-600 hover:text-primary">Information Sharing</a>
                  <a href="#security" className="block text-gray-600 hover:text-primary">Data Security</a>
                  <a href="#rights" className="block text-gray-600 hover:text-primary">Your Rights</a>
                  <a href="#contact" className="block text-gray-600 hover:text-primary">Contact Us</a>
                </nav>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 md:p-8">
                <div className="prose prose-lg max-w-none">
                  
                  <section id="overview" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Overview</h2>
                    <p className="text-gray-700 mb-4">
                      MGL Experiences Ltd ("we", "us", "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage with us as a potential or existing partner.
                    </p>
                    <p className="text-gray-700">
                      As a UK-based company, we comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
                    </p>
                  </section>

                  <section id="information" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
                    
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Business Partner Information</h3>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Company name and contact details</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Contact person name, email, and phone number</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Company registration and business details</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Communication history and correspondence</span>
                      </li>
                    </ul>

                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Website Usage Information</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">IP address and browser type</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Pages visited and time spent</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Referral source and navigation patterns</span>
                      </li>
                    </ul>
                  </section>

                  <section id="usage" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Information</h2>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Purpose</th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Legal Basis</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            <td className="px-4 py-4 text-sm text-gray-700">Responding to partnership inquiries</td>
                            <td className="px-4 py-4 text-sm text-gray-700">Legitimate interest</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-4 text-sm text-gray-700">Managing business relationships</td>
                            <td className="px-4 py-4 text-sm text-gray-700">Contractual necessity</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-4 text-sm text-gray-700">Improving our website and services</td>
                            <td className="px-4 py-4 text-sm text-gray-700">Legitimate interest</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-4 text-sm text-gray-700">Compliance with legal obligations</td>
                            <td className="px-4 py-4 text-sm text-gray-700">Legal obligation</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-4 text-sm text-gray-700">Marketing communication (with consent)</td>
                            <td className="px-4 py-4 text-sm text-gray-700">Consent</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section id="sharing" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing</h2>
                    <p className="text-gray-700 mb-4">
                      We do not sell your personal information. We may share information with:
                    </p>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Service Providers</h4>
                        <p className="text-sm text-gray-600">IT support, analytics, and communication tools</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Legal Authorities</h4>
                        <p className="text-sm text-gray-600">When required by law or regulation</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Business Partners</h4>
                        <p className="text-sm text-gray-600">Only with your explicit consent</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Professional Advisors</h4>
                        <p className="text-sm text-gray-600">Legal, financial, or consulting services</p>
                      </div>
                    </div>
                  </section>

                  <section id="security" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Security Measures</h3>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Encryption of sensitive data</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Secure access controls and authentication</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Regular security assessments and updates</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Data retention policies in compliance with UK law</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  <section id="rights" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights Under UK GDPR</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Right to Access</h4>
                        <p className="text-sm text-gray-600">Request a copy of your personal data</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Right to Rectification</h4>
                        <p className="text-sm text-gray-600">Correct inaccurate or incomplete data</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Right to Erasure</h4>
                        <p className="text-sm text-gray-600">Request deletion of your data</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Right to Restrict Processing</h4>
                        <p className="text-sm text-gray-600">Limit how we use your data</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Right to Data Portability</h4>
                        <p className="text-sm text-gray-600">Receive your data in a portable format</p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">Right to Object</h4>
                        <p className="text-sm text-gray-600">Object to certain processing activities</p>
                      </div>
                    </div>
                  </section>

                  <section id="contact" className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact Us</h2>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Protection Officer</h3>
                      <p className="text-gray-700 mb-4">
                        For privacy-related inquiries or to exercise your rights, contact our Data Protection Officer:
                      </p>
                      <div className="space-y-2">
                        <p className="text-gray-700">
                          <strong>Email:</strong> privacy@mglexperiences.com
                        </p>
                        <p className="text-gray-700">
                          <strong>Postal Address:</strong> Data Protection Officer, MGL Experiences Ltd, [UK Address]
                        </p>
                        <p className="text-gray-700">
                          <strong>Response Time:</strong> We aim to respond within 30 days
                        </p>
                      </div>
                    </div>
                  </section>

                </div>
              </div>

              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Link
                      to="/legal-compliance"
                      className="text-sm text-primary font-medium hover:text-primary-dark"
                    >
                      ← Back to Legal & Compliance
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}